import React from 'react'
import { Button, Form, Input, Modal } from 'antd'
import type { InputRef } from 'antd/lib/input'
import { useTranslation } from '@nic/commons'
import { useAuth } from '@nic/kc-auth'
import { NicScope } from '@nic/nic-api/types'
import { useHistory } from 'react-router'
import { findRequestByDomain } from '../../Services/requestSearch'

type RequestLookupVariant = 'desktop' | 'mobile'

interface RequestDomainLookupModalProps {
  variant?: RequestLookupVariant
  buttonColor?: string
  className?: string
}
 
export const RequestDomainLookupModal: React.FC<RequestDomainLookupModalProps> = ({
  variant = 'desktop',
  buttonColor,
  className,
}) => {
  const { keycloak } = useAuth()
  const history = useHistory()
  const t = useTranslation()
  const realm = (keycloak?.realm as NicScope) || 'registrar'
  const [form] = Form.useForm()
  const inputRef = React.useRef<InputRef>(null)

  const [visible, setVisible] = React.useState(false)
  const [domain, setDomain] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (visible) {
      const timer = window.setTimeout(() => {
        inputRef.current?.focus()
      }, 0)

      return () => window.clearTimeout(timer)
    }
  }, [visible])

  const resetState = () => {
    setDomain('')
    setError(null)
    setLoading(false)
    form.resetFields()
  }

  const closeModal = () => {
    setVisible(false)
    setLoading(false)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDomain(event.target.value)
    setError(null)
  }

  const handleSubmit = async () => {
    if (!domain.trim()) {
      setError(t('requestDomainLookupModal.validation.required'))
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await findRequestByDomain(domain, realm)

      if (!result?.requestId) {
        setError(t('requestDomainLookupModal.validation.notFound', { domain }))
        return
      }

      closeModal()
      history.push(`/${realm}/requests/${result.requestId}`)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404) {
        setError(t('requestDomainLookupModal.validation.notFound', { domain }))
      } else {
        setError(t('requestDomainLookupModal.validation.generic'))
      }
    } finally {
      setLoading(false)
    }
  }

  const buttonProps =
    variant === 'desktop'
      ? {
          type: 'primary' as const,
          ghost: true,
          style: {
            color: '#ffffff',
            borderColor: '#ffffff',
          },
        }
      : {
          type: 'primary' as const,
          block: true,
          style: buttonColor
            ? {
                backgroundColor: buttonColor,
                borderColor: buttonColor,
              }
            : undefined,
        }

  return (
    <>
      <Button
        {...buttonProps}
        className={className}
        onClick={() => setVisible(true)}
        data-testid='request-domain-lookup-trigger'
      >
        {t('requestDomainLookupModal.trigger')}
      </Button>
      <Modal
        visible={visible}
        title={t('requestDomainLookupModal.title')}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={t('requestDomainLookupModal.submit')}
        cancelText={t('requestDomainLookupModal.cancel')}
        confirmLoading={loading}
        maskClosable={true}
        destroyOnClose={false}
        afterClose={resetState}
      >
        <Form form={form} layout='vertical' onFinish={handleSubmit}>
          <Form.Item
            label={t('requestDomainLookupModal.label')}
            validateStatus={error ? 'error' : undefined}
            help={error ?? t('requestDomainLookupModal.description')}
          >
            <Input
              ref={inputRef}
              value={domain}
              onChange={handleChange}
              placeholder={t('requestDomainLookupModal.placeholder')}
              autoComplete='off'
            />
          </Form.Item>
          <Button htmlType='submit' style={{ display: 'none' }}>
            {t('requestDomainLookupModal.submit')}
          </Button>
        </Form>
      </Modal>
    </>
  )
}
